import { useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, Loader2, XCircle } from "lucide-react";
import {
  downloadLandResearchReport,
  landResearchApi,
  type LandResearchProgressResponse,
} from "@/lib/api";
import { openSse } from "@/lib/sse";
import { useSession } from "@/lib/useAuth";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Alert } from "@/components/ui/Alert";

// Map agent tool names to product-research-facing 中文 labels ("爬取了哪些数据"),
// so the page never leaks raw tool-function names. Unknown tools fall back to
// the event-supplied label or the raw name.
const TOOL_LABELS: Record<string, string> = {
  list_uploaded_files: "读取上传文件",
  inspect_file: "解析上传数据",
  map_geocode: "项目坐标落位行政区",
  map_batch_geocode: "批量坐标落位",
  map_place_search: "搜索周边配套",
  map_nearby_search: "爬取 3-5km 周边配套",
  map_distance_matrix: "测算道路距离",
  map_route_plan: "规划道路路线",
  render_static_map: "生成周边地图",
  render_map_view: "生成区域地图",
  enterprise_count_near: "统计周边分行业企业数",
  enterprise_qualified_count: "统计资质客户数",
  region_industrial_metrics: "取区域工业指标",
  industry_structure_analysis: "分析产业结构",
  land_small_parcel_share: "统计小幅用地供应",
  generate_demand_model_report: "生成需求底表",
  qianfan_web_summary: "公开资料检索",
  read_web_page: "读取权威网页正文",
  query_user_data_source: "查询数据源",
  run_python: "几何/数值测算",
  run_rule: "应用研判规则",
  generate_land_judgement_report: "生成研判报告",
};

interface ToolEntry {
  key: string;
  label: string;
  running: boolean;
  ok: boolean;
}

type Status = "connecting" | "live" | "done" | "error";

interface State {
  status: Status;
  tools: ToolEntry[];
  errorMsg: string | null;
}

type Action =
  | { kind: "live" }
  | { kind: "tool_start"; tool: string; idx: number; label?: string }
  | { kind: "tool_end"; tool: string; idx: number; ok: boolean }
  | { kind: "done" }
  | { kind: "closed" }
  | { kind: "error"; message: string };

function toolLabel(tool: string, label?: string): string {
  return TOOL_LABELS[tool] ?? label ?? tool;
}

// Render the agent's 研判结论 markdown (headings / tables / bold / hr) with the
// same visual language as the chat canvas, scoped down to the elements the
// verdict actually emits.

function reduce(state: State, action: Action): State {
  switch (action.kind) {
    case "live":
      return state.status === "connecting" ? { ...state, status: "live" } : state;
    case "tool_start": {
      const key = `${action.tool}#${action.idx}`;
      if (state.tools.some((t) => t.key === key)) return state;
      return {
        ...state,
        status: "live",
        tools: [
          ...state.tools,
          { key, label: toolLabel(action.tool, action.label), running: true, ok: false },
        ],
      };
    }
    case "tool_end": {
      const key = `${action.tool}#${action.idx}`;
      return {
        ...state,
        tools: state.tools.map((t) =>
          t.key === key ? { ...t, running: false, ok: action.ok } : t,
        ),
      };
    }
    case "done":
      return { ...state, status: state.status === "error" ? "error" : "done" };
    case "closed":
      // Stream ended without message_done/error: an interruption, NOT completion.
      // (Completion is only ever signalled by the message_done frame.)
      return state.status === "done" || state.status === "error"
        ? state
        : { ...state, status: "error", errorMsg: "连接中断，研判未完成；请刷新页面重试。" };
    case "error":
      return { ...state, status: "error", errorMsg: action.message };
    default:
      return state;
  }
}

const INITIAL: State = { status: "connecting", tools: [], errorMsg: null };

function formatElapsed(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return `${hours} 小时 ${minutes} 分钟`;
  return `${minutes} 分钟`;
}

function formatActivityTime(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}

export function LandResearchProgressPage(): JSX.Element {
  const { convId = "" } = useParams<{ convId: string }>();
  const session = useSession();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reduce, INITIAL);
  const [progress, setProgress] = useState<LandResearchProgressResponse | null>(null);

  useEffect(() => {
    if (!convId) return;
    let alive = true;
    const refreshProgress = async (): Promise<void> => {
      try {
        const snapshot = await landResearchApi.progress(session.token, convId);
        if (!alive) return;
        setProgress(snapshot);
        if (snapshot.status === "done") dispatch({ kind: "done" });
        if (snapshot.status === "error") {
          dispatch({ kind: "error", message: "数据搜索任务执行失败，请查看最近活动。" });
        }
      } catch {
        // The SSE remains the completion authority if the snapshot request fails.
      }
    };
    void refreshProgress();
    const timer = window.setInterval(() => void refreshProgress(), 5_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [convId, session.token]);

  useEffect(() => {
    if (!convId) return;
    const ctrl = new AbortController();
    void openSse({
      url: landResearchApi.streamUrl(convId),
      token: session.token,
      signal: ctrl.signal,
      handlers: {
        onFrame: (frame) => {
          const d = (frame.data ?? {}) as Record<string, unknown>;
          const tool = String(d.tool ?? "");
          const idx = Number(d.step_index ?? 0);
          switch (frame.type) {
            case "turn_start":
              dispatch({ kind: "live" });
              break;
            case "tool_call_start":
              dispatch({ kind: "tool_start", tool, idx, label: d.label as string | undefined });
              break;
            case "tool_call_end":
              dispatch({ kind: "tool_end", tool, idx, ok: d.outcome !== "error" });
              break;
            case "message_done":
              dispatch({ kind: "done" });
              break;
            case "final":
              dispatch({ kind: "done" });
              break;
            case "error":
              dispatch({ kind: "error", message: String(d.message ?? "数据搜索出错") });
              break;
            default:
              break;
          }
        },
        onError: (err) => {
          if (ctrl.signal.aborted) return; // effect cleanup (StrictMode remount), not a failure
          dispatch({ kind: "error", message: err instanceof Error ? err.message : "连接中断" });
        },
        onClose: () => {
          if (ctrl.signal.aborted) return; // aborted by us — the replacement stream owns the state
          dispatch({ kind: "closed" });
        },
      },
    });
    return () => ctrl.abort();
  }, [convId, session.token]);


  const onDownload = (): void => {
    void downloadLandResearchReport(session.token, convId, "地块数据.xlsx");
  };

  const live = state.status === "connecting" || state.status === "live";
  const running = state.tools.find((t) => t.running);
  const liveHint =
    state.status === "connecting"
      ? "连接中…"
      : running
        ? `正在：${running.label}`
        : "正在整理已获取的数据…";

  return (
    <div className="h-full overflow-y-auto bg-canvas">
      <div className="mx-auto max-w-3xl space-y-5 px-6 py-6">
        <header className="flex items-center gap-2 text-[13px] text-zinc-500">
          <button onClick={() => navigate("/land")} className="rounded-md p-1.5 hover:bg-zinc-100">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span>地块数据</span>
          <span className="text-zinc-300">/</span>
          <span className="text-zinc-800">搜索进度</span>
        </header>

        <Card>
          <CardHeader
            title={progress?.status === "done" ? "数据搜索完成" : "数据搜索进度"}
            description={
              state.status === "done"
                ? "外部数据已获取完毕，可下载数据 Excel。"
                : state.status === "error"
                  ? "数据搜索中断。"
                  : progress
                    ? `第 ${progress.stage_index}/${progress.stage_total} 阶段 · 已用时 ${formatElapsed(progress.elapsed_seconds)}`
                    : "正在读取任务进度。"
            }
          />
          <CardBody className="space-y-2.5">
            {progress ? (
              <>
                <div className="border-b border-cardline pb-3">
                  <div className="text-sm font-semibold text-zinc-900">{progress.stage_label}</div>
                  <div className="mt-1 text-sm text-zinc-600">{progress.detail}</div>
                  {progress.updated_at ? (
                    <div className="mt-1 text-xs text-zinc-400">
                      最近更新 {formatActivityTime(progress.updated_at)}
                    </div>
                  ) : null}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {progress.stages.map((stage, index) => (
                    <div key={stage.key} className="flex min-h-8 items-center gap-2 text-sm">
                      {stage.status === "done" ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-good" />
                      ) : stage.status === "running" ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-500" />
                      ) : stage.status === "error" ? (
                        <XCircle className="h-4 w-4 shrink-0 text-bad" />
                      ) : (
                        <span className="h-4 w-4 shrink-0 rounded-full border border-zinc-300" />
                      )}
                      <span
                        className={
                          stage.status === "running"
                            ? "font-medium text-brand-800"
                            : stage.status === "pending"
                              ? "text-zinc-400"
                              : "text-zinc-700"
                        }
                      >
                        {index + 1}. {stage.label}
                      </span>
                    </div>
                  ))}
                </div>
                {progress.recent.length ? (
                  <div className="border-t border-cardline pt-3">
                    <div className="mb-2 text-xs font-semibold text-zinc-500">最近活动</div>
                    <div className="space-y-2">
                      {progress.recent.map((activity, index) => (
                        <div
                          key={`${activity.ts}-${index}`}
                          className="flex min-h-6 items-start gap-2 text-xs text-zinc-600"
                        >
                          <span className="w-12 shrink-0 tabular-nums text-zinc-400">
                            {formatActivityTime(activity.ts)}
                          </span>
                          {activity.status === "running" ? (
                            <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-brand-500" />
                          ) : (
                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-good" />
                          )}
                          <span>{activity.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
            {state.tools.map((t) => (
              <div key={t.key} className="flex items-center gap-2.5 text-sm">
                {t.running ? (
                  <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                ) : t.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-good" />
                ) : (
                  <XCircle className="h-4 w-4 text-bad" />
                )}
                <span className={t.running ? "text-zinc-500" : "text-zinc-800"}>{t.label}</span>
              </div>
            ))}
            {/* Always-moving liveness line so LLM-thinking gaps don't look stuck. */}
            {live ? (
              <div className="flex items-center gap-2.5 border-t border-cardline pt-2.5 text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                <span>{liveHint}</span>
              </div>
            ) : null}
          </CardBody>
        </Card>

        {state.status === "error" ? <Alert tone="error">{state.errorMsg}</Alert> : null}

        {state.status === "done" ? (
          <div className="flex flex-col gap-3 rounded-lg border border-[#CBE5CC] bg-[#F1F9F1] px-5 py-4 shadow-card sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-[#3F7E42]">
              数据 Excel 已生成：区位维度 / 四至道路（含卫星图）/ 企业 5km / 区县指标 / 土地成交，每项数据附来源，不含研判结论。
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button onClick={() => navigate(`/land/${convId}/data`)}>
                <Eye className="h-4 w-4" />
                查看数据
              </Button>
              <Button onClick={onDownload} variant="secondary">
                下载数据 Excel
              </Button>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  );
}
